import React, { useMemo } from 'react';

import { Box } from '@chakra-ui/react';

import { DataTypeTags, StackedCell } from 'components/DataDisplay';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { CELL_PADDING_Y } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Identity/Principals/utils';
import { useDataTypeClassifiers } from 'containers/Visibility/Data/Components/hooks/useDataTypeClassifiers';

import { DataNameCell } from './DataNameCell';
import { GrantorsLine } from './GrantorsLine';
import { OwnAccessCell } from './OwnAccessCell';
import { PrivilegeBadges } from './PrivilegeBadges';
import { DataAccessRow } from './utils';

// Name, Own Access, Contains, High Privileges, Sensitive. The role names
// under Own Access and Contains were cut off, so those two get more room than
// the mockup gave them; the badges fit in a tenth of the row.
const WIDTHS = ['30%', '25%', '25%', '10%', '10%'];

// The Principals tab's shorter rows (12px instead of the table's 16px above
// and below), so more rows fit in the panel. Every level uses them, so open
// rows keep the same height as the database rows.
export const ROW_CELL_STYLES = {
  paddingTop: CELL_PADDING_Y,
  paddingBottom: CELL_PADDING_Y,
};

/**
 * The columns every level of the tree shares, so nested rows line up with the
 * database rows: Name, Own Access, Contains, High Privileges and Sensitive.
 */
export const useDataAccessColumns = (
  onToggle: (row: DataAccessRow) => void,
): TableColumnProps<DataAccessRow>[] => {
  const { classifiers } = useDataTypeClassifiers();

  return useMemo(
    () => [
      {
        header: <Box pl={7}>Name</Box>,
        render: ({ row }) => <DataNameCell row={row} onToggle={onToggle} />,
      },
      {
        header: 'Sensitivity',
        render: ({ row }) =>
          row.sensitivityAvailable ? (
            <DataTypeTags
              dataTypes={row.sensitiveDataTypes}
              classifiers={classifiers}
            />
          ) : (
            <CustomTooltip label="No completed collection to read sensitivity from">
              <Box color="gray.400">Unavailable</Box>
            </CustomTooltip>
          ),
        align: 'left',
      },
      {
        header: 'Own Access',
        align: 'left',
        render: ({ row }) => <OwnAccessCell row={row} />,
      },
      {
        header: 'Contains',
        align: 'left',
        // A table has nothing inside it; who grants it shows under Own Access.
        // Roles inside show as "via 3 roles", which opens their list, so the
        // line doesn't repeat the first role named under Own Access.
        render: ({ row }) =>
          row.kind === 'table' ? (
            <Box color="gray.400">—</Box>
          ) : (
            <StackedCell
              upper={row.contains}
              lower={<GrantorsLine grantors={row.grantors} compact />}
              showLowerTooltip={false}
            />
          ),
      },
      {
        header: 'High Privileges',
        render: ({ row }) => <PrivilegeBadges levels={row.levels} />,
      },
    ],
    [classifiers, onToggle],
  );
};
